import { useEffect, useRef } from 'react';
import { useRepositioningContext } from './RepositioningContext';
import decommissionAnimationDetector from './decommissionAnimationDetector';
import {
  isUnitStillOnBattlefield,
  resolveViewerSlotOwnerPlayerId,
} from '../utils/battleLethalDeathFx';

const { isDecommissionedUnitInHand } = decommissionAnimationDetector;

/**
 * Hook to detect unit deaths by tracking slot states
 * Compares previous slot state (had unit) with current (empty) to detect deaths
 * @param {Array} opponentFront - Opponent front zone units
 * @param {Array} opponentFlank - Opponent flank zone units
 * @param {Array} yourFront - Your front zone units
 * @param {Array} yourFlank - Your flank zone units
 * @param {Object} gameState
 * @param {string|null} userId
 * @param {Object} deadAttackersRef - Ref containing Set of attacker instanceIds that died (for synchronous checking)
 * @param {Object} deadTargetsRef - Ref containing Set of target instanceIds that died (for synchronous checking)
 * @param {Object} strategemTargetedUnitsRef - Ref containing Set of unit instanceIds targeted by strategem animations
 * @param {Object} pendingUnitDamageFxSlotsRef - Ref containing slot keys with queued attack/strategem damage FX
 * @param {Object} bullyDamagePendingInstanceIdsRef - Ref containing instanceIds awaiting Professional Bully fist sequence
 * @param {Object} suppressRedeemSlotDeathInstanceIdsRef - Ref containing redeem units in staged death (skip slot-death FX)
 * @param {Object} completedRedeemDeathInstanceIdsRef - Ref containing redeem units whose staged death finished (permanent skip)
 * @param {Object} clearSlotPreviousStateOutRef - Ref populated with (zoneName, index) => void to drop stale slot tracking
 * @param {((payload: { zoneName: string; index: number; unitSnapshot: Object; damageAmount?: number }) => void)|null} onUnitLethalDeath - on-card damage pop + white fade (same timing as attack kills)
 * @param {((payload: { zoneName: string; index: number; image: string }) => void)|null} onExploreUnknownMemoryFade - fade-out overlay when EU Memory token leaves a slot without damage death VFX
 */
export const useSlotDeathDetection = (
  opponentFront,
  opponentFlank,
  yourFront,
  yourFlank,
  gameState = null,
  userId = null,
  deadAttackersRef = null,
  deadTargetsRef = null,
  strategemTargetedUnitsRef = null,
  pendingUnitDamageFxSlotsRef = null,
  bullyDamagePendingInstanceIdsRef = null,
  suppressRedeemSlotDeathInstanceIdsRef = null,
  completedRedeemDeathInstanceIdsRef = null,
  clearSlotPreviousStateOutRef = null,
  onUnitLethalDeath = null,
  onExploreUnknownMemoryFade = null
) => {
  // Track slot states: "zone_index" -> { instanceId, health, name, unitSnapshot }
  const previousSlotStatesRef = useRef(new Map());
  const repositioningContext = useRepositioningContext();

  const clearSlotPreviousState = (zoneName, index) => {
    if (!zoneName || index == null) {
      throw new Error('clearSlotPreviousState: zoneName and index are required');
    }
    previousSlotStatesRef.current.delete(`${zoneName}_${index}`);
  };

  useEffect(() => {
    if (clearSlotPreviousStateOutRef) {
      clearSlotPreviousStateOutRef.current = clearSlotPreviousState;
    }
  }, [clearSlotPreviousStateOutRef]);

  const triggerEuMemoryFade = (name, idx, previous) => {
    if (typeof onExploreUnknownMemoryFade !== 'function' || !previous?.memoryImage) {
      return;
    }
    onExploreUnknownMemoryFade({
      zoneName: name,
      index: idx,
      image: previous.memoryImage,
    });
  };

  useEffect(() => {
    const allZones = [
      { name: 'opponent_front', units: opponentFront },
      { name: 'opponent_flank', units: opponentFlank },
      { name: 'your_front', units: yourFront },
      { name: 'your_flank', units: yourFlank },
    ];

    allZones.forEach(({ name, units }) => {
      units.forEach((unit, idx) => {
        const slotKey = `${name}_${idx}`;
        const previous = previousSlotStatesRef.current.get(slotKey);

        if (unit && unit.instanceId) {
          // Slot has unit - update tracking
          previousSlotStatesRef.current.set(slotKey, {
            instanceId: unit.instanceId,
            health: unit.health || 0,
            name: unit.name || 'Unknown',
            isMission5Corn: unit.isMission5Corn === true,
            isExploreUnknownMemory: unit.isExploreUnknownMemory === true,
            memoryImage: typeof unit.image === 'string' ? unit.image : '',
            unitSnapshot: { ...unit },
          });
        } else if (previous && previous.health > 0) {
          // Slot was empty - but had unit before
          const slotOwnerPlayerId = resolveViewerSlotOwnerPlayerId(name, gameState, userId);

          // Reposition: unit still on board (any zone/slot) — movement overlay only, no death/damage VFX.
          let isRepositioning = Boolean(
            gameState
            && slotOwnerPlayerId
            && previous.instanceId
            && isUnitStillOnBattlefield(gameState, slotOwnerPlayerId, previous.instanceId)
          );

          if (
            !isRepositioning
            && repositioningContext?.isUnitRepositioning
            && slotOwnerPlayerId
            && previous.instanceId
          ) {
            isRepositioning = repositioningContext.isUnitRepositioning(
              slotOwnerPlayerId,
              previous.instanceId
            );
          }

          if (isRepositioning) {
            console.log(`🔄 DEATH DETECTION: Unit ${previous.name} was repositioned from ${slotKey}, skipping death/damage VFX`);
            previousSlotStatesRef.current.delete(slotKey);
            return;
          }
          
          // ✅ FIX: DELETE death animations for attackers that die during an attack
          // Check persistent ref directly (synchronous, no callback timing issues)
          // The two-phase attack system handles attacker death timing
          const isAttacker = deadAttackersRef?.current?.has(previous.instanceId);
          if (isAttacker) {
            console.log(`💀 BATTLEUI: DELETING death animation for attacker ${previous.name} in slot ${slotKey} - handled by two-phase attack system`);
            if (previous.isExploreUnknownMemory === true) {
              triggerEuMemoryFade(name, idx, previous);
            }
            previousSlotStatesRef.current.delete(slotKey);
            return; // DELETE - don't create animation at all
          }
          
          // ✅ FIX: DELETE death animations for attack targets
          // Check persistent ref directly (synchronous, no callback timing issues)
          // The attack animation system handles damage animations for attack targets
          // This prevents 20+ duplicate animations when target dies
          const isAttackTarget = deadTargetsRef?.current?.has(previous.instanceId);
          if (isAttackTarget) {
            console.log(`💀 BATTLEUI: DELETING death animation for attack target ${previous.name} in slot ${slotKey} - handled by attack animation system`);
            if (previous.isExploreUnknownMemory === true) {
              triggerEuMemoryFade(name, idx, previous);
            }
            previousSlotStatesRef.current.delete(slotKey);
            return; // DELETE - don't create animation at all
          }
          
          // ✅ FIX: DELETE death animations for strategem-targeted units
          // The strategem target animation system handles damage animations via onDamageAtTarget
          const isStrategemTarget = strategemTargetedUnitsRef?.current?.has(previous.instanceId);
          if (isStrategemTarget) {
            console.log(`💀 BATTLEUI: DELETING death animation for strategem target ${previous.name} in slot ${slotKey} - handled by strategem animation system`);
            if (previous.isExploreUnknownMemory === true) {
              triggerEuMemoryFade(name, idx, previous);
            }
            previousSlotStatesRef.current.delete(slotKey);
            return; // DELETE - don't create animation at all
          }

          // Decommission (Kinetron Blast 111, Siege Tank 82, HTS 354, etc.): unit returned to hand — fly-out only.
          if (gameState && previous.instanceId && slotOwnerPlayerId) {
            const ownerHand = gameState.state?.[slotOwnerPlayerId]?.hand;
            if (isDecommissionedUnitInHand(ownerHand, previous)) {
              console.log(`🔄 DEATH DETECTION: Unit ${previous.name} was decommissioned from ${slotKey}, skipping death/damage VFX`);
              previousSlotStatesRef.current.delete(slotKey);
              return;
            }
          }

          // Mission 5 corn cuts should not trigger normal death/damage visuals/sounds.
          if (previous.isMission5Corn === true) {
            previousSlotStatesRef.current.delete(slotKey);
            return;
          }

          // Explore Unknown Memory: fade out instead of damage death VFX
          if (previous.isExploreUnknownMemory === true) {
            triggerEuMemoryFade(name, idx, previous);
            previousSlotStatesRef.current.delete(slotKey);
            return;
          }

          if (pendingUnitDamageFxSlotsRef?.current?.has(slotKey)) {
            console.log(`💀 BATTLEUI: Skipping slot death FX for ${slotKey} — attack/strategem damage FX already queued`);
            previousSlotStatesRef.current.delete(slotKey);
            return;
          }

          const isBullyPending = bullyDamagePendingInstanceIdsRef?.current?.has(previous.instanceId);
          if (isBullyPending) {
            console.log(`👊 BATTLEUI: Deferring slot death FX for ${previous.name} in ${slotKey} — Professional Bully fist sequence pending`);
            previousSlotStatesRef.current.delete(slotKey);
            return;
          }

          const isRedeemStagedDeath = suppressRedeemSlotDeathInstanceIdsRef?.current?.has(previous.instanceId);
          if (isRedeemStagedDeath) {
            console.log(`♻️ BATTLEUI: Skipping slot death FX for ${previous.name} in ${slotKey} — staged redeem death sequence`);
            previousSlotStatesRef.current.delete(slotKey);
            return;
          }

          const isCompletedRedeemDeath = completedRedeemDeathInstanceIdsRef?.current?.has(previous.instanceId);
          if (isCompletedRedeemDeath) {
            console.log(`♻️ BATTLEUI: Skipping slot death FX for ${previous.name} in ${slotKey} — redeem death sequence already completed`);
            previousSlotStatesRef.current.delete(slotKey);
            return;
          }
          
          // Slot was empty - but had unit before = unit died!
          if (typeof onUnitLethalDeath === 'function') {
            console.log(`💀 BATTLEUI: Lethal death sequence for ${previous.name} in slot ${slotKey} (had ${previous.health} health)`);
            onUnitLethalDeath({
              zoneName: name,
              index: idx,
              unitSnapshot: previous.unitSnapshot || {
                instanceId: previous.instanceId,
                name: previous.name,
                health: previous.health,
              },
              damageAmount: previous.health > 0 ? previous.health : undefined,
            });
          }
          // Clear tracking for this slot
          previousSlotStatesRef.current.delete(slotKey);
        } else if (!unit && previous) {
          // Slot empty with a zero-health snapshot — drop stale tracking (no death VFX).
          previousSlotStatesRef.current.delete(slotKey);
        } else if (!unit && !previous) {
          // Always empty - no change
        }
      });

      // Clean up tracking for slots beyond array length
      for (let idx = units.length; idx < 5; idx++) {
        const slotKey = `${name}_${idx}`;
        previousSlotStatesRef.current.delete(slotKey);
      }
    });
  }, [opponentFront, opponentFlank, yourFront, yourFlank, gameState, userId, repositioningContext, deadAttackersRef, deadTargetsRef, strategemTargetedUnitsRef, pendingUnitDamageFxSlotsRef, bullyDamagePendingInstanceIdsRef, onUnitLethalDeath, onExploreUnknownMemoryFade]);
};
